/**
 * Sidebar group expansion state.
 *
 * Owner rule: EVERY group (hubs, sub-groups, Favoris, Récents) starts collapsed
 * on every load — the active route's hub included. The state is the set of
 * EXPANDED keys, kept in memory only (never persisted), so an empty set means
 * "all collapsed" whatever hub ids the workspace preset produces.
 */

import { useCallback, useEffect, useState } from 'react';

/** Keys persisted by the old "remember collapse state" sidebar — purged on mount. */
export const LEGACY_NAV_GROUP_KEYS = [
  'psn-nav-collapsed-hubs',
  'psn-nav-collapsed-groups',
  'karexia.nav.moreFolded',
] as const;

/** Shortcut sections rendered above the hubs; expanded together with them. */
export const NAV_SHORTCUT_KEYS = ['sec.favorites', 'sec.recents'] as const;

function clearLegacyNavGroupState(): void {
  try {
    LEGACY_NAV_GROUP_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage disabled: nothing was persisted anyway */
  }
}

/**
 * @param collapsibleKeys every hub + sub-group key ("expand all" opens them all)
 * @param topLevelKeys the hub keys: "all collapsed" means no hub is open — a
 *   sub-group left open inside a closed hub is invisible and must not flip the
 *   "Tout agrandir / Tout réduire" label.
 */
export function useNavGroupExpansion(collapsibleKeys: readonly string[], topLevelKeys: readonly string[] = collapsibleKeys) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  useEffect(clearLegacyNavGroupState, []);

  const isOpen = useCallback((key: string) => expanded.has(key), [expanded]);

  const toggle = useCallback(
    (key: string) =>
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  const open = useCallback((key: string) => setExpanded((prev) => new Set(prev).add(key)), []);

  const allCollapsed = topLevelKeys.every((k) => !expanded.has(k));

  const setAllCollapsed = useCallback(
    (collapse: boolean) =>
      setExpanded(collapse ? new Set<string>() : new Set([...collapsibleKeys, ...NAV_SHORTCUT_KEYS])),
    [collapsibleKeys],
  );

  return { isOpen, toggle, open, allCollapsed, setAllCollapsed };
}
